import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBankAccounts, BankAccount } from '@/hooks/use-bank-accounts';
import { useExchangeRates } from '@/hooks/finance/useExchangeRates';
import { CreateTransferInput } from '@/hooks/finance/useInternalTransfers';
import { format } from 'date-fns';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: CreateTransferInput) => Promise<any>;
  isSubmitting: boolean;
}

export function NewTransferDialog({ open, onOpenChange, onSubmit, isSubmitting }: Props) {
  const { data: accounts = [] } = useBankAccounts(true);
  const { rates } = useExchangeRates();

  const [fromAccountId, setFromAccountId] = useState('');
  const [toAccountId, setToAccountId] = useState('');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [transferDate, setTransferDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [referenceNumber, setReferenceNumber] = useState('');
  const [description, setDescription] = useState('');
  const [rateSource, setRateSource] = useState<'system' | 'manual'>('system');

  const fromAccount = accounts.find(a => a.id === fromAccountId);
  const toAccount = accounts.find(a => a.id === toAccountId);
  const fromCurrency = fromAccount?.currency || 'ETB';
  const toCurrency = toAccount?.currency || 'ETB';
  const isCrossCurrency = fromCurrency !== toCurrency;

  // Auto-lookup system rate when accounts/date change
  useEffect(() => {
    if (!isCrossCurrency || rateSource !== 'system' || !fromAmount) return;

    // Find applicable rate
    const pair = `${fromCurrency}/${toCurrency}`;
    const reversePair = `${toCurrency}/${fromCurrency}`;
    const rateType = fromAccount?.exchange_rate_type || 'bank_rate';

    const applicableRates = rates.filter(r =>
      (r.currency_pair === pair || r.currency_pair === reversePair) &&
      r.rate_type === rateType &&
      r.effective_date <= transferDate
    ).sort((a, b) => b.effective_date.localeCompare(a.effective_date));

    if (applicableRates.length > 0) {
      const rate = applicableRates[0];
      const amt = parseFloat(fromAmount);
      if (!isNaN(amt)) {
        if (rate.currency_pair === pair) {
          setToAmount((amt * rate.rate).toFixed(2));
        } else {
          setToAmount((amt / rate.rate).toFixed(2));
        }
      }
    }
  }, [fromAccountId, toAccountId, fromAmount, transferDate, rateSource, rates, isCrossCurrency, fromCurrency, toCurrency, fromAccount?.exchange_rate_type]);

  // Same currency: pre-fill toAmount only when user hasn't manually edited it
  const [toAmountManuallyEdited, setToAmountManuallyEdited] = useState(false);
  useEffect(() => {
    if (!isCrossCurrency && fromAmount && !toAmountManuallyEdited) {
      setToAmount(fromAmount);
    }
  }, [fromAmount, isCrossCurrency, toAmountManuallyEdited]);

  // Reset manual edit flag when accounts change
  useEffect(() => {
    setToAmountManuallyEdited(false);
  }, [fromAccountId, toAccountId]);

  const transferFee = fromAmount && toAmount && parseFloat(fromAmount) > parseFloat(toAmount)
    ? (parseFloat(fromAmount) - parseFloat(toAmount)).toFixed(2)
    : null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const from = parseFloat(fromAmount);
    const to = parseFloat(toAmount);
    if (isNaN(from) || isNaN(to) || from <= 0 || to <= 0) return;
    if (!referenceNumber.trim()) return;

    const exchangeRate = isCrossCurrency ? to / from : 1;

    await onSubmit({
      transfer_date: transferDate,
      from_account_id: fromAccountId,
      to_account_id: toAccountId,
      from_amount: from,
      to_amount: to,
      from_currency: fromCurrency,
      to_currency: toCurrency,
      exchange_rate: exchangeRate,
      exchange_rate_source: isCrossCurrency ? rateSource : 'manual',
      reference_number: referenceNumber || undefined,
      description: description || undefined,
    });

    // Reset form
    setFromAccountId('');
    setToAccountId('');
    setFromAmount('');
    setToAmount('');
    setReferenceNumber('');
    setDescription('');
    onOpenChange(false);
  };

  const availableToAccounts = accounts.filter(a => a.id !== fromAccountId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Internal Transfer</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>From Account</Label>
              <Select value={fromAccountId} onValueChange={setFromAccountId}>
                <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name} ({a.currency || 'ETB'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>To Account</Label>
              <Select value={toAccountId} onValueChange={setToAccountId}>
                <SelectTrigger><SelectValue placeholder="Select destination" /></SelectTrigger>
                <SelectContent>
                  {availableToAccounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name} ({a.currency || 'ETB'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Transfer Date</Label>
            <Input type="date" value={transferDate} onChange={e => setTransferDate(e.target.value)} required />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Amount Sent ({fromCurrency})</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={fromAmount}
                onChange={e => setFromAmount(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Amount Received ({toCurrency})</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={toAmount}
                onChange={e => {
                  setToAmount(e.target.value);
                  setToAmountManuallyEdited(true);
                  if (isCrossCurrency) setRateSource('manual');
                }}
                placeholder="0.00"
                required
              />
            </div>
          </div>

          {transferFee && (
            <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 rounded-md px-3 py-2">
              <span>Transfer Fee:</span>
              <span className="font-mono font-semibold">{parseFloat(transferFee).toLocaleString()} {fromCurrency}</span>
              <span className="text-xs text-muted-foreground ml-1">(will be recorded as expense)</span>
            </div>
          )}

          {isCrossCurrency && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>Rate source:</span>
              <Button
                type="button"
                variant={rateSource === 'system' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRateSource('system')}
              >
                System Rate
              </Button>
              <Button
                type="button"
                variant={rateSource === 'manual' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setRateSource('manual')}
              >
                Manual
              </Button>
              {fromAmount && toAmount && (
                <span className="ml-auto font-mono">
                  1 {fromCurrency} = {(parseFloat(toAmount) / parseFloat(fromAmount)).toFixed(4)} {toCurrency}
                </span>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>Reference Number <span className="text-destructive">*</span></Label>
            <Input value={referenceNumber} onChange={e => setReferenceNumber(e.target.value)} placeholder="Enter reference number" required />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional notes" rows={2} />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting || !fromAccountId || !toAccountId || !fromAmount || !toAmount}>
              {isSubmitting ? 'Recording...' : 'Record Transfer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
